import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { readAuthoredItem } from "../../../../api/content/authoring/processAuthoringApi";
import { newClientKey } from "../../../../api/threads/threadsApi";
import { useProjects } from "../../../../projects/projectContext";
import { useLoad } from "../../../../shared/loading/useLoad";
import { usePaneRedirect } from "../../../../shell/workspace";
import LoadStatus from "../../../../ui/feedback/LoadStatus";
import { preparationPath } from "../intentAddress";
import { editIntent } from "./authorChoices";

/**
 * An address that opened an item's form or editor without a step (`/items/<item>@<version>/edit`,
 * a Workflow's `?mode=edit`), replaced by the edit's preparation with the manual way chosen
 * (ADR-0031 D2, CP-06). The preparation creates nothing until the edit is saved.
 */
export default function EditAddressRedirect({ reference, carry = null }: { reference: string; carry?: string | null }) {
  const { t } = useTranslation();
  const projectId = useProjects().selectedProjectId;
  const redirect = usePaneRedirect();
  const [requestKey] = useState(newClientKey);
  const load = useCallback((signal: AbortSignal) => readAuthoredItem(reference, signal), [reference]);
  const { state, reload } = useLoad(load);
  const item = state.status === "loaded" ? state.value : null;
  useEffect(() => {
    if (item === null || projectId === null) return;
    const intent = editIntent(projectId, { itemKind: item.kind, name: item.name, reference: item.reference }, null, carry === null ? { way: "by_hand" } : { way: "by_hand", carry });
    redirect(preparationPath({ intent, requestKey }));
  }, [item, projectId, carry, requestKey, redirect]);
  return <LoadStatus state={state} loadingLabel={t("common.loading")} loadingClassName="py-10" onRetry={reload} />;
}

/** `/items/<item>@<version>/edit`. */
export function ItemEditAddress() {
  const { itemReference = "" } = useParams();
  return <EditAddressRedirect reference={itemReference} />;
}
